import { useState } from "react";
import { toast } from "sonner";
import { Gift as GiftIcon, Sparkles, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const TIERS = [
    { amount: 50, label: "Thoughtful", description: "A treat for a friend or new-parent gift.", accent: "from-gold/20 to-gold/5" },
    { amount: 100, label: "Signature", description: "Our most-loved gift — a full home refresh.", accent: "from-gold/40 to-gold/10", featured: true },
    { amount: 200, label: "Luxurious", description: "A deep clean and pampering for someone special.", accent: "from-gold/60 to-gold/20" },
];

export default function Gift() {
    const [amount, setAmount] = useState(100);
    const [form, setForm] = useState({
        purchaser_name: "",
        purchaser_email: "",
        recipient_name: "",
        recipient_email: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/gift-vouchers", { ...form, amount });
            setDone(true);
            toast.success("Gift request received — we'll be in touch shortly.");
        } catch {
            toast.error("Something went wrong. Please try again or call us.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="gift-page" className="min-h-screen bg-cream">
            <Navbar variant="solid" />
            <section className="pt-32 pb-24 px-6 md:px-12">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-14">
                        <GiftIcon className="w-8 h-8 text-gold-dark mx-auto mb-4" />
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Gift Vouchers</span>
                        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 tracking-tight">
                            Give the gift of a <span className="italic text-gold-dark">spotless home</span>.
                        </h1>
                        <p className="font-outfit text-base md:text-lg text-muted-foreground mt-4 max-w-xl mx-auto">
                            Perfect for weddings, new parents, moving-in gifts and corporate thank-yous. We&apos;ll deliver a personalised voucher code straight to your recipient.
                        </p>
                    </div>

                    {done ? (
                        <div data-testid="gift-thankyou" className="text-center bg-white border border-gold p-10 md:p-14 max-w-2xl mx-auto">
                            <CheckCircle2 className="w-12 h-12 text-gold-dark mx-auto mb-4" />
                            <h2 className="font-playfair text-3xl text-navy mb-3">Gift request received.</h2>
                            <p className="font-outfit text-muted-foreground mb-6">
                                Our team will be in touch within one working day to arrange payment and deliver the voucher to your recipient. Thank you for choosing Signature Shine &amp; Co.
                            </p>
                            <a href="/" className="inline-flex items-center gap-2 bg-navy text-gold border border-gold px-6 py-3 font-outfit text-sm uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors">
                                Back to home
                            </a>
                        </div>
                    ) : (
                        <>
                            {/* Tiers */}
                            <div className="grid md:grid-cols-3 gap-5 mb-12">
                                {TIERS.map((t) => (
                                    <button
                                        key={t.amount}
                                        onClick={() => setAmount(t.amount)}
                                        data-testid={`gift-tier-${t.amount}`}
                                        className={`text-left p-8 border transition-all bg-gradient-to-b ${t.accent} ${
                                            amount === t.amount ? "border-gold ring-2 ring-gold" : "border-cream-border hover:border-gold/60"
                                        }`}
                                    >
                                        {t.featured && (
                                            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-gold-dark">Most popular</span>
                                        )}
                                        <div className="font-playfair text-lg text-navy mt-1">{t.label}</div>
                                        <div className="font-playfair text-4xl text-gold-dark my-3">€{t.amount}</div>
                                        <div className="font-outfit text-sm text-muted-foreground">{t.description}</div>
                                    </button>
                                ))}
                            </div>

                            {/* Custom amount */}
                            <div className="text-center mb-10">
                                <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">Or enter a custom amount</label>
                                <div className="inline-flex items-center gap-2">
                                    <span className="font-playfair text-2xl text-gold-dark">€</span>
                                    <input
                                        data-testid="gift-custom-amount"
                                        type="number"
                                        min={25}
                                        max={500}
                                        value={amount}
                                        onChange={(e) => setAmount(Math.max(25, Math.min(500, Number(e.target.value) || 0)))}
                                        className="w-32 border border-cream-border bg-white px-4 py-3 font-outfit text-navy text-center text-xl focus:outline-none focus:border-gold"
                                    />
                                </div>
                                <p className="font-outfit text-[11px] text-muted-foreground mt-2">Between €25 and €500</p>
                            </div>

                            {/* Form */}
                            <form onSubmit={submit} className="bg-white border border-cream-border p-8 md:p-10 grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="gift-form">
                                <GField label="Your name" testid="gift-purchaser-name" value={form.purchaser_name} onChange={(v) => update({ purchaser_name: v })} required />
                                <GField label="Your email" testid="gift-purchaser-email" type="email" value={form.purchaser_email} onChange={(v) => update({ purchaser_email: v })} required />
                                <GField label="Recipient's name" testid="gift-recipient-name" value={form.recipient_name} onChange={(v) => update({ recipient_name: v })} required />
                                <GField label="Recipient's email" testid="gift-recipient-email" type="email" value={form.recipient_email} onChange={(v) => update({ recipient_email: v })} required />
                                <div className="sm:col-span-2">
                                    <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">Personal message (optional)</label>
                                    <textarea
                                        data-testid="gift-message"
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => update({ message: e.target.value })}
                                        placeholder="Happy anniversary, treat yourself…"
                                        className="w-full border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
                                    />
                                </div>
                                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-cream-border">
                                    <div>
                                        <span className="uppercase tracking-[0.2em] text-xs font-bold text-navy">Gift total</span>
                                        <div className="font-playfair text-3xl text-gold-dark">€{amount.toFixed(2)}</div>
                                    </div>
                                    <button
                                        type="submit"
                                        data-testid="gift-submit"
                                        disabled={submitting}
                                        className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 font-outfit font-semibold text-sm uppercase tracking-wider hover:bg-gold-light disabled:opacity-50"
                                    >
                                        {submitting ? "Sending…" : "Request gift voucher"} <Sparkles className="w-4 h-4" />
                                    </button>
                                </div>
                                <p className="sm:col-span-2 text-[11px] text-muted-foreground text-center">
                                    Our team will confirm payment by phone or bank transfer, then email the voucher code to your recipient — usually within one working day.
                                </p>
                            </form>
                        </>
                    )}
                </div>
            </section>
            <Footer />
        </div>
    );
}

function GField({ label, testid, value, onChange, type = "text", required = false }) {
    return (
        <div>
            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">{label}</label>
            <input
                data-testid={testid}
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
            />
        </div>
    );
}
